import { useEffect, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  calendarApi,
  homeQuery,
  integrationKey,
  type IntegrationSummary,
} from '../../lib/calendar-api';
import styles from './Integrations.module.css';
import { IntegrationBar } from './IntegrationBar';
import { CalendarCard, MeetingCard } from './CalendarCards';
import { Emails } from '../email/Emails';
import { GmailSetupCard, GmailSetupResult, useGmailConnection, type useGmailSetup } from '../email/GmailConnection';
import { CalendarPreview } from './CalendarPreview';
export function integrationSummary(
  service: IntegrationSummary | undefined,
  provider: string,
  timezone = 'UTC',
) {
  if (!service) return 'Checking connection…';
  const name = provider === 'gmail' ? 'Gmail' : 'Google Calendar';
  if (service.state === 'failed') return `Couldn’t load ${name}. Try Refresh.`;
  if (service.state === 'not_configured') return `${name} setup is unavailable`;
  if (service.state === 'disconnected') return `Connect ${name}`;
  if (service.state === 'reconnect_required') return `Reconnect ${name}`;
  if (['paused', 'disconnecting'].includes(service.state))
    return `${name} is ${service.state}`;
  const count = service.totalCount ?? 0,
    drafts = service.readyDraftCount ?? 0;
  if (
    service.state === 'importing' ||
    (provider !== 'gmail' && !service.complete && !service.lastSyncedAt)
  )
    return `Scanning ${name}…`;
  const today = service.todayCount ?? 0;
  const summary =
    provider === 'gmail'
      ? count
        ? `${count} email${count === 1 ? '' : 's'} to review`
        : 'All caught up — no client emails to review'
      : `${today} meeting${today === 1 ? '' : 's'} today | ${count} this week`;
  let next = '';
  if (provider !== 'gmail' && today === 0 && service.nextMeeting) {
    const meeting = service.nextMeeting;
    const at = new Date(meeting.startsAt);
    const dateKey = (date: Date) =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    const now = new Date();
    const localToday = dateKey(now);
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const value = (key: string) =>
      Number(parts.find((p) => p.type === key)?.value);
    const tomorrow = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(
      new Date(Date.UTC(value('year'), value('month') - 1, value('day') + 1)),
    );
    const day =
      dateKey(at) === localToday
        ? 'today'
        : dateKey(at) === tomorrow
          ? 'tomorrow'
          : at.toLocaleDateString([], {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              timeZone: timezone,
            });
    const time = at
      .toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
      })
      .replace(':00', '');
    next = ` · Next: ${meeting.payload.summary || 'Meeting'} ${day}${meeting.payload.start?.date ? '' : ` at ${time}`}`;
  }

  return `${summary}${next}${drafts ? ` · ${drafts} ${provider === 'gmail' ? 'replies ready' : 'ready to schedule'}` : ''}${service.lastError ? ' · Couldn’t refresh' : ''}`;
}
export function HomeIntegrations({
  onThreadSelected,
  activeProvider,
  onActiveProviderChange,
  gmailSetup: sharedGmailSetup,
  calendarConnect: sharedCalendarConnect,
}: {
  onThreadSelected?: (id: string | undefined) => void;
  activeProvider?: 'gmail' | 'google_calendar' | null;
  onActiveProviderChange?: (provider: 'gmail' | 'google_calendar' | null) => void;
  gmailSetup?: ReturnType<typeof useGmailSetup>;
  calendarConnect?: ReturnType<typeof useCalendarConnect>;
}) {
  const home = useQuery(homeQuery()),
    client = useQueryClient();
  const gmailConnection = useGmailConnection();
  const initTimezone = useMutation({
    mutationFn: calendarApi.preferences,
    onSuccess: () => client.invalidateQueries({ queryKey: integrationKey() }),
  });
  useEffect(() => {
    if (
      home.data &&
      !home.data.preferences.timezoneSaved &&
      !initTimezone.isPending &&
      !initTimezone.isSuccess &&
      !initTimezone.isError
    )
      initTimezone.mutate({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
  }, [home.data]);
  const localConnect = useCalendarConnect();
  const connect = sharedCalendarConnect ?? localConnect;
  const disconnect = useMutation({
    mutationFn: calendarApi.disconnect,
    onSuccess: () => client.invalidateQueries({ queryKey: integrationKey() }),
  });
  const calendar = home.data?.calendar,
    gmail = home.data?.gmail;
  const needsSetup = (service: IntegrationSummary | undefined) =>
    (home.isError && !service) ||
    (!!service &&
      [
        'failed',
        'disconnected',
        'reconnect_required',
        'not_configured',
      ].includes(service.state));
  const calendarSetup = needsSetup(calendar),
    gmailSetup = needsSetup(gmail);
  const connected =
    calendar && ['active', 'importing', 'paused'].includes(calendar.state);
  const events = useInfiniteQuery({
    queryKey: [...integrationKey(), 'events'],
    queryFn: ({ pageParam }) => calendarApi.events(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    enabled: !!connected,
    refetchInterval: 30_000,
    retry: false,
  });
  const drafts = useQuery({
    queryKey: [...integrationKey(), 'drafts'],
    queryFn: calendarApi.drafts,
    enabled: !!connected,
    refetchInterval: 5000,
    retry: false,
  });
  const [reviewedDrafts, setReviewedDrafts] = useState<string[]>([]);
  useEffect(() => {
    if (drafts.data)
      setReviewedDrafts((previous) => {
        const next = [
          ...new Set([...previous, ...drafts.data.items.map((d) => d.id)]),
        ];
        return next.length === previous.length ? previous : next;
      });
  }, [drafts.data]);
  const deep = new URLSearchParams(window.location.search),
    calendarLink = deep.get('calendarEventId'),
    draftLink = deep.get('calendarDraftId');
  const [items, timezone] = [
    events.data?.pages.flatMap((p) => p.items) ?? [],
    home.data?.preferences.timezone ?? 'UTC',
  ];
  const disclosure = (provider: 'gmail' | 'google_calendar') => activeProvider === undefined ? {} : {
    open: activeProvider === provider,
    hidden: activeProvider !== provider,
    bare: true,
    onOpenChange: (open: boolean) => onActiveProviderChange?.(open ? provider : null),
  };
  return (
    <div className={styles.homeBars}>
      {gmailSetup ? <GmailSetupCard initiallyOpen={!!deep.get('gmail')} {...disclosure('gmail')} {...(sharedGmailSetup ? { setup: sharedGmailSetup } : {})} /> : <IntegrationBar
        provider="gmail"
        {...disclosure('gmail')}
        refreshing={gmail?.state==='importing' || ['queued','running'].includes(gmail?.activeRun?.status ?? '')}
        summary={
          home.isError
            ? 'Couldn’t load Gmail. Try Refresh.'
            : integrationSummary(gmail, 'gmail')
        }
        initiallyOpen={!!deep.get('emailThreadId') || !!deep.get('gmail')}
      >
        <GmailSetupResult />
        {gmail?.connections?.map((c) => (
          <p key={c.id}>
            {c.accountEmail} · <a href="/app/settings/gmail">Manage Gmail</a>
          </p>
        ))}
        {gmailConnection.data?.connections.map(c => <p key={c.id} className={styles.meta}>
          {c.autoDraft && c.fundingAuthorized && gmailConnection.data?.draftingEnabled && ['active', 'importing'].includes(c.status)
            ? 'Automatic replies are on. Every send needs your approval.'
            : 'Automatic reply preparation is off or paused.'} <a href="/app/settings/gmail">Reply settings</a>
        </p>)}
        {gmail?.setupIssue && (
          <p>
            {typeof gmail.setupIssue === 'string'
              ? gmail.setupIssue
              : gmail.setupIssue.message}
          </p>
        )}
        {gmail?.lastError && (
          <p>
            Couldn’t refresh. Last success:{' '}
            {gmail.lastSyncedAt
              ? new Date(gmail.lastSyncedAt).toLocaleString()
              : 'not yet synced'}
            .
          </p>
        )}
        <Emails {...(onThreadSelected ? { onThreadSelected } : {})} />
      </IntegrationBar>}
      <IntegrationBar
        provider="google_calendar"
        {...disclosure('google_calendar')}
        refreshing={calendar?.state==='importing' || ['queued','running'].includes(calendar?.activeRun?.status ?? '')}
        {...(calendarSetup
          ? {
              title:
                calendar?.state === 'reconnect_required'
                  ? 'Reconnect Google Calendar'
                  : 'Connect Google Calendar',
              description: home.isError && !calendar ? home.error.message : calendar?.state==='failed' ? 'Calendar status could not be checked.' : 'See meetings with your CRM contacts and schedule meetings you approve.',
            }
          : {})}
        summary={
          home.isError
            ? 'Couldn’t load Calendar. Try Refresh.'
            : integrationSummary(
                calendar,
                'google_calendar',
                home.data?.preferences.timezone ?? 'UTC',
              )
        }
        initiallyOpen={!!calendarLink || !!draftLink || !!deep.get('calendar')}
        onConnect={calendarSetup ? () => connect.mutate() : undefined}
        connecting={connect.isPending}
        connectionError={connect.error?.message}
        connectionDisabled={calendar?.state === 'not_configured' || calendar?.state === 'failed' || home.isError}
      >
        {deep.get('calendar') === 'connection_failed' && <p role="alert">Google Calendar wasn’t connected. Try again and approve the requested Calendar access.</p>}
        {calendarSetup && <CalendarPreview />}
        {!calendarSetup && (
          <p className={styles.meta}>Today + next 7 days · {timezone}</p>
        )}
        {calendar?.connections?.map((c) => (
          <p key={c.id}>
            {c.accountEmail} · Primary calendar{' '}
            <button
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate(c.id)}
            >
              Disconnect Calendar
            </button>
          </p>
        ))}
        {calendar?.setupIssue && (
          <p>
            {typeof calendar.setupIssue === 'string'
              ? calendar.setupIssue
              : calendar.setupIssue.message}
          </p>
        )}
        {calendar?.lastError && (
          <p role="status">
            Couldn’t refresh. Last success:{' '}
            {calendar.lastSyncedAt
              ? new Date(calendar.lastSyncedAt).toLocaleString()
              : 'not yet synced'}
            .
          </p>
        )}
        {disconnect.error && (
          <p role="alert">
            {disconnect.error.message}
          </p>
        )}
        {(calendarLink || draftLink) && (
          <CalendarCard
            eventId={calendarLink ?? undefined}
            draftId={draftLink ?? undefined}
          />
        )}
        {reviewedDrafts
          .filter((id) => id !== draftLink)
          .map((id) => (
            <CalendarCard key={id} draftId={id} />
          ))}
        {items
          .filter((e) => e.id !== calendarLink)
          .map((e) => (
            <MeetingCard key={e.id} event={e} timezone={timezone} />
          ))}
        {calendar?.complete && !items.length && (
          <p>
            No upcoming meetings with your CRM contacts. Click Refresh to
            rescan.
          </p>
        )}
        {calendar?.eligibleAddressCount === 0 && (
          <p>
            Add email addresses to your CRM contacts to find their meetings.
          </p>
        )}
        {events.error && (
          <p role="alert">Couldn’t load meetings. Try Refresh.</p>
        )}
        {events.hasNextPage && (
          <button
            disabled={events.isFetchingNextPage}
            onClick={() => void events.fetchNextPage()}
          >
            More meetings
          </button>
        )}
      </IntegrationBar>
    </div>
  );
}

export function useCalendarConnect() {
  return useMutation({ mutationFn: calendarApi.connect, onSuccess: data => window.location.assign(data.authorizationUrl) });
}
